import { byId, clear, el, setBusy, setHidden, setText } from "../dom";
import { formatCountdown, formatDayTime, isExpired } from "../format";
import type { FleetResponse, GeneratedCode, Vehicle } from "../types";
import { errorState } from "./states";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

const CODE_TTL_MINUTES = 480;
const CODE_TTL_MS = CODE_TTL_MINUTES * 60_000;

export interface OperationHandlers {
  onReloadFleet(): void;
  onGenerate(): void;
  onCopyCode(): void;
}

export interface OperationSelection {
  vehicleId: string;
  routeId: string;
}

export interface OperationController {
  setFleetStatus(status: LoadStatus, fleet: FleetResponse | null, error: string | null): void;
  getSelection(): OperationSelection | null;
  setGenerating(busy: boolean): void;
  setCode(code: GeneratedCode | null): void;
  refreshClock(): void;
}

export function initOperation(handlers: OperationHandlers): OperationController {
  const fleetSummary = byId<HTMLParagraphElement>("fleet-summary");
  const reloadFleetButton = byId<HTMLButtonElement>("reload-fleet");
  const vehicleSelect = byId<HTMLSelectElement>("vehicle-select");
  const routeSelect = byId<HTMLSelectElement>("route-select");
  const generateButton = byId<HTMLButtonElement>("generate-code");
  const codeBox = byId<HTMLDivElement>("code-box");
  const codeValue = byId<HTMLParagraphElement>("code-value");
  const codeOwner = byId<HTMLParagraphElement>("code-owner");
  const codeExpiry = byId<HTMLParagraphElement>("code-expiry");
  const codeMeter = byId<HTMLSpanElement>("code-meter");
  const copyButton = byId<HTMLButtonElement>("copy-code");
  const placeholder = byId<HTMLParagraphElement>("code-placeholder");

  const errorSlot = el("div", { className: "operation__error" });
  vehicleSelect.closest(".field-grid")?.insertAdjacentElement("afterend", errorSlot);

  let fleet: FleetResponse | null = null;
  let code: GeneratedCode | null = null;
  let optionsKey = "";

  function fillSelect(select: HTMLSelectElement, options: readonly { value: string; label: string }[]): void {
    const previous = select.value;
    clear(select);
    if (options.length === 0) {
      select.append(el("option", { text: "Sin opciones" }));
      select.disabled = true;
      return;
    }
    for (const option of options) {
      const element = el("option", { text: option.label });
      element.value = option.value;
      select.append(element);
    }
    select.disabled = false;
    if (options.some((option) => option.value === previous)) select.value = previous;
  }

  function renderSelects(): void {
    if (fleet === null) return;
    const key = `${fleet.vehicles.map((v: Vehicle) => v.id).join(",")}|${fleet.routes.map((r) => r.id).join(",")}`;
    if (key === optionsKey) return;
    optionsKey = key;
    fillSelect(
      vehicleSelect,
      fleet.vehicles.map((vehicle) => ({ value: vehicle.id, label: `${vehicle.label} · ${vehicle.plate}` })),
    );
    fillSelect(
      routeSelect,
      fleet.routes.map((route) => ({ value: route.id, label: route.name })),
    );
  }

  function showPlaceholder(message: string): void {
    setText(placeholder, message);
    setHidden(placeholder, false);
  }

  function renderCode(): void {
    if (code === null) {
      setHidden(codeBox, true);
      return;
    }
    setHidden(codeBox, false);
    setText(codeValue, code.code);
    setText(codeOwner, `${code.vehicle.label} · ${code.vehicle.plate} · ${code.route.name}`);
    refreshClock();
  }

  function refreshClock(): void {
    if (code === null) return;
    const expiresAt = Date.parse(code.expires_at);
    if (Number.isNaN(expiresAt)) {
      setText(codeExpiry, "Sin fecha de vencimiento");
      codeMeter.style.width = "100%";
      return;
    }
    if (isExpired(code.expires_at)) {
      setText(codeExpiry, `Venció el ${formatDayTime(code.expires_at)} · genera uno nuevo`);
      codeMeter.style.width = "0%";
      return;
    }
    setText(codeExpiry, `Vence ${formatCountdown(code.expires_at)} · a las ${formatDayTime(code.expires_at)}`);
    const fraction = Math.min(Math.max((expiresAt - Date.now()) / CODE_TTL_MS, 0), 1);
    codeMeter.style.width = `${Math.round(fraction * 100)}%`;
  }

  reloadFleetButton.addEventListener("click", handlers.onReloadFleet);
  generateButton.addEventListener("click", handlers.onGenerate);
  copyButton.addEventListener("click", handlers.onCopyCode);

  return {
    setFleetStatus(status, nextFleet, error) {
      fleet = nextFleet;
      setBusy(reloadFleetButton, status === "loading");
      clear(errorSlot);

      if (status === "loading" || status === "idle") {
        setText(fleetSummary, "Cargando flota…");
        clear(vehicleSelect);
        vehicleSelect.append(el("option", { text: "Cargando…" }));
        vehicleSelect.disabled = true;
        clear(routeSelect);
        routeSelect.append(el("option", { text: "Cargando…" }));
        routeSelect.disabled = true;
        generateButton.disabled = true;
        return;
      }

      if (status === "error" || nextFleet === null) {
        setText(fleetSummary, "Flota sin cargar");
        optionsKey = "";
        clear(vehicleSelect);
        vehicleSelect.append(el("option", { text: "Sin datos" }));
        vehicleSelect.disabled = true;
        clear(routeSelect);
        routeSelect.append(el("option", { text: "Sin datos" }));
        routeSelect.disabled = true;
        generateButton.disabled = true;
        errorSlot.append(
          errorState({
            title: "No pudimos cargar la flota",
            message: error ?? "Revisa la conexión e intenta de nuevo.",
            onRetry: handlers.onReloadFleet,
          }),
        );
        return;
      }

      optionsKey = "";
      renderSelects();
      const total = nextFleet.vehicles.length + nextFleet.routes.length;
      setText(
        fleetSummary,
        total === 0
          ? `${nextFleet.operator.name} · sin vehículos ni rutas`
          : `${nextFleet.operator.name} · ${nextFleet.vehicles.length} vehículos · ${nextFleet.routes.length} rutas`,
      );
      generateButton.disabled = nextFleet.vehicles.length === 0 || nextFleet.routes.length === 0;
    },

    getSelection() {
      if (vehicleSelect.value === "" || routeSelect.value === "") return null;
      return { vehicleId: vehicleSelect.value, routeId: routeSelect.value };
    },

    setGenerating(busy) {
      setBusy(generateButton, busy);
    },

    setCode(nextCode) {
      code = nextCode;
      renderCode();
      setText(
        generateButton,
        nextCode === null ? "Generar código del turno" : "Generar otro código",
      );
      if (nextCode === null) {
        showPlaceholder("Elige vehículo y ruta y genera el código de 6 dígitos que el conductor reclama desde su teléfono.");
      } else {
        setHidden(placeholder, true);
      }
    },

    refreshClock,
  };
}
